/**
 * PageNav — the top bar of the pages that are not the feed (/home, /you, /@<handle>): the mark
 * (the way to the landing page), the three places a person moves between (Home, the Catalogue,
 * the feed), and the account door. The founder's ruling, 27 Sep: the profile page had only "Open
 * the feed", so getting anywhere else meant the brand or the back button. Every page passes its
 * own feed and sign-in doors, since each carries its own attribution (?via=, ?r=), and names the
 * one it is, which reads aria-current. Imports nothing that emits a row (tests/unit/join.test.ts
 * pins the list).
 */
import { Show } from 'solid-js';

export type NavPage = 'home' | 'you' | 'maker' | 'catalogue';

export function PageNav(props: {
  /** The page this bar is on; it reads aria-current="page". */
  current: NavPage;
  /** The feed door with this page's attribution, e.g. /feed?via=home. */
  feed: string;
  /** The sign-in door for a stranger; the account door is /you. */
  signin?: string;
  isAccount?: boolean;
  handle?: string | null;
  /** The feed link's test id, one per page (home-feed-top, profile-feed-top, maker-feed-top). */
  feedTestId: string;
  whoTestId?: string;
}) {
  const who = () => (props.isAccount ? '/you' : props.signin ?? '/you');
  const whoLabel = () => (props.handle ? `Your profile, @${props.handle}` : props.isAccount ? 'Your profile' : 'Sign in');
  return (
    <header class="top-bar">
      <a class="brand" data-ui href="/" data-testid="brand-link" aria-label="About Blitz">
        <img src="/icons/icon.svg" alt="" />
        BLITZ
      </a>
      <nav class="top-nav" aria-label="Pages" data-testid="top-nav">
        <a href="/home" aria-current={props.current === 'home' ? 'page' : undefined} data-testid="nav-home">
          Home
        </a>
        <a href="/catalogue" aria-current={props.current === 'catalogue' ? 'page' : undefined} data-testid="nav-catalogue">
          Catalogue
        </a>
      </nav>
      <div class="top-actions">
        <a class="btn primary" href={props.feed} data-testid={props.feedTestId}>
          Feed
        </a>
        <a
          class="icon-btn who"
          classList={{ signed: !!props.isAccount }}
          href={who()}
          aria-current={props.current === 'you' ? 'page' : undefined}
          data-testid={props.whoTestId ?? 'nav-who'}
          aria-label={whoLabel()}
          title={props.handle ? `@${props.handle}` : props.isAccount ? 'Your profile' : 'Sign in'}
        >
          <Show
            when={props.handle}
            fallback={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="8" r="4" />
                <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
            }
          >
            {(h) => h()[0].toUpperCase()}
          </Show>
        </a>
      </div>
    </header>
  );
}
